export type DocTypeHint = 'book' | 'form' | 'generic'

/**
 * Very lightweight heuristic detector for form-like documents based on OCR text.
 * It looks for multiple lines that contain long runs of dots or underscores used as fields.
 */
export function detectDocTypeFromText(rawText: string): DocTypeHint {
  if (!rawText || !rawText.trim()) return 'generic'

  const lines = rawText.split(/\r?\n/)

  let dottedFieldLines = 0
  let totalNonEmpty = 0
  let longProseLines = 0
  let sentenceEndingLines = 0
  let continuationPairs = 0
  let previousNonEmpty: string | null = null

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
    totalNonEmpty++

    // Match lines that have a label plus a run of 3+ dots/underscores,
    // or standalone long runs of dots/underscores (fields).
    if (/[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9]+[^A-Za-z0-9]*[._]{3,}\s*$/.test(trimmed) ||
        /^[._]{5,}\s*$/.test(trimmed)) {
      dottedFieldLines++
    }

    if (trimmed.length >= 45) {
      longProseLines++
    }

    if (/[.!?]["'”’»)]?$/.test(trimmed)) {
      sentenceEndingLines++
    }

    if (previousNonEmpty) {
      const prevEndsMidSentence = /[a-ząćęłńóśźż,;:]$/.test(previousNonEmpty)
      const currentStartsLowercase = /^[a-ząćęłńóśźż]/.test(trimmed)
      if (prevEndsMidSentence && currentStartsLowercase) {
        continuationPairs++
      }
    }
    previousNonEmpty = trimmed
  }

  if (totalNonEmpty === 0) return 'generic'

  const ratio = dottedFieldLines / totalNonEmpty

  // Heuristic: if at least ~10% of non-empty lines look like fields and
  // there are at least 3 such lines, treat as a form.
  if (dottedFieldLines >= 3 && ratio >= 0.1) {
    return 'form'
  }

  // Lightweight prose/book detector.
  // We classify as "book" when the text looks like running narrative:
  // - many longer lines,
  // - multiple sentence endings,
  // - and visible wrapped-line continuations.
  const longLineRatio = longProseLines / totalNonEmpty
  const strongContinuationSignal = continuationPairs >= 2 && sentenceEndingLines >= 1
  const balancedNarrativeSignal =
    longLineRatio >= 0.25 && sentenceEndingLines >= 1 && continuationPairs >= 1
  const looksLikeBookProse =
    totalNonEmpty >= 4 &&
    dottedFieldLines === 0 &&
    (balancedNarrativeSignal || strongContinuationSignal)

  if (looksLikeBookProse) {
    return 'book'
  }

  return 'generic'
}

/**
 * Pick one document type from what each page looked like.
 *
 * The document used to be classified by the first page that returned any text,
 * and that answer was then locked for every page after it. A 45-page scan opening
 * with a title page or an index is therefore classified by its least
 * representative page - and the consequence is larger than it sounds, because the
 * `generic` normalization prompt joins wrapped lines far less readily than the
 * `book` one. Measured on one page: `book` joined 19 wraps, `generic` joined 0 on
 * the same text. A title page that reads as `generic` can cost an entire book its
 * line joining, which is the "brak skladu i ladu" complaint. P2.5.
 *
 * `generic` is the detector's fall-through - what it returns when nothing matched,
 * including a page with too little text to judge. So it is counted as an abstention
 * rather than a vote: a majority among the pages that actually looked like
 * something, and `generic` only when no page did.
 *
 * A tie goes to `form`, deliberately. Joining lines in a form merges labels with
 * their values and is hard to undo; leaving a book's lines unjoined is a worse
 * read but loses nothing. The cheaper mistake wins the toss.
 */
export function pickDocumentType(perPageTypes: DocTypeHint[]): DocTypeHint {
  let book = 0
  let form = 0
  for (const type of perPageTypes) {
    if (type === 'book') book++
    else if (type === 'form') form++
  }

  if (book === 0 && form === 0) return 'generic'
  return book > form ? 'book' : 'form'
}
